import { create } from 'zustand';
import { apiClient } from '@/api/client';

interface SettingsStore {
  settings: Record<string, string>;
  loaded: boolean;

  load: () => Promise<void>;
  save: (updates: Record<string, string>) => Promise<void>;
  get: (key: string, defaultValue: string) => string;
  getNumber: (key: string, defaultValue: number) => number;
  getBool: (key: string, defaultValue: boolean) => boolean;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  settings: {},
  loaded: false,

  load: async () => {
    try {
      const settings = await apiClient.get<Record<string, string>>('/api/settings');
      set({ settings, loaded: true });
    } catch {
      set({ loaded: true }); // use defaults
    }
  },

  save: async (updates) => {
    try {
      await apiClient.put('/api/settings', updates);
      set((s) => ({ settings: { ...s.settings, ...updates } }));
    } catch (err) {
      console.error('Failed to save settings:', err);
      throw err;
    }
  },

  get: (key, defaultValue) => {
    return get().settings[key] ?? defaultValue;
  },

  getNumber: (key, defaultValue) => {
    const val = get().settings[key];
    if (val === undefined) return defaultValue;
    const num = parseFloat(val);
    return isNaN(num) ? defaultValue : num;
  },

  getBool: (key, defaultValue) => {
    const val = get().settings[key];
    if (val === undefined) return defaultValue;
    return val === 'true';
  },
}));
